/**
 * FullCalendar - Shared Calendar Configuration
 * Configuration de base commune pour les calendriers FullCalendar
 */

import { Calendar } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { buildBookedDays } from './bookingSlots.js';
import { ensureRentableRange } from './apiClient.js';

/**
 * Default FullCalendar configuration
 */
export const DEFAULT_FULLCALENDAR_CONFIG = {
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin, multiMonthPlugin],
    locale: 'fr',
    firstDay: 1,
    headerToolbar: {
        left: 'prev,next',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek'
    },
    buttonText: {
        today: 'Aujourd\'hui',
        month: 'Mois',
        week: 'Semaine',
        day: 'Jour'
    }
};

/**
 * Fetch booked days from API
 * @param {string} rentableId - ID of the rentable resource
 * @param {function} callback - Callback function to handle the result
 */
export function fetchBookedDays(rentableId, callback) {
    if (!rentableId) {
        callback({full: [], partial: []});
        return;
    }

    // Par défaut : le mois courant et les deux suivants (ce que la vue affiche au
    // chargement). La navigation élargit la fenêtre au fil des besoins.
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 3, 0);

    fetchBookedDaysForRange(rentableId, start, end, callback);
}

/**
 * Charge la plage demandée si elle manque, puis rend la classification de **tout**
 * ce qui est connu du rentable (le cache est cumulatif : repeindre est idempotent,
 * donc l'ordre d'arrivée des réponses lors d'une navigation rapide n'a pas d'importance).
 *
 * @param {string} rentableId
 * @param {Date} startDate - début de la plage visible
 * @param {Date} endDate - fin de la plage visible
 * @param {function} callback
 */
export function fetchBookedDaysForRange(rentableId, startDate, endDate, callback) {
    if (!rentableId) {
        callback({full: [], partial: []});
        return;
    }

    ensureRentableRange(rentableId, startDate, endDate)
        .then(rows => {
            // Classification partagée : full = jour saturé, partial = jour encore libre.
            // Cohérent avec les pickers du formulaire (flatpickr-init-app.js).
            callback(buildBookedDays(rows));
        })
        .catch(() => {
            callback({full: [], partial: []});
        });
}

/**
 * Format date for different contexts
 * @param {Date} date - Date to format
 * @param {string} type - Format type ('french' or 'english')
 * @returns {object} Formatted date and time
 */
export function formatDate(date, type = 'french') {
    const pad = (num) => num.toString().padStart(2, '0');
    
    if (type === 'french') {
        return {
            date: `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`,
            time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
        };
    } else {
        return {
            date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
            time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
        };
    }
}

/**
 * Check if two date ranges overlap
 * @param {object} newEvent - New event with start/end dates
 * @param {array} existingEvents - Array of existing events
 * @returns {boolean} True if overlapping
 */
export function isOverlapping(newEvent, existingEvents) {
    return existingEvents.some(existingEvent => {
        const newStart = new Date(newEvent.start);
        const newEnd = new Date(newEvent.end);
        const existingStart = new Date(existingEvent.start);
        const existingEnd = new Date(existingEvent.end);
        
        return newEnd >= existingStart && 
               newStart <= existingEnd && 
               existingEvent.backgroundColor !== '#323232';
    });
}

/**
 * Find rentable ID from DOM elements
 * @returns {string|null} Rentable ID if found
 */
export function findRentableId() {
    // Try input field first
    let rentableId = document.querySelector('input[name="rentableId"]')?.value;
    
    // Try event start date input
    if (!rentableId) {
        const eventStartInput = document.getElementById('eventStartDate');
        if (eventStartInput) {
            rentableId = eventStartInput.dataset.rentableid;
        }
    }
    
    // Try fullcalendar element
    if (!rentableId) {
        const fullCalendar = document.getElementById('fullCalendarGemengApp');
        if (fullCalendar) {
            rentableId = fullCalendar.dataset.rentableid;
        }
    }
    
    return rentableId;
}

/**
 * Create calendar instance with common configuration
 * @param {string} elementId - DOM element ID
 * @param {object} customConfig - Custom configuration to override defaults
 * @returns {Calendar|null} FullCalendar instance
 */
export function createCalendar(elementId, customConfig = {}) {
    const calendarElement = document.getElementById(elementId);
    if (!calendarElement) return null;
    
    const config = { ...DEFAULT_FULLCALENDAR_CONFIG, ...customConfig };
    return new Calendar(calendarElement, config);
}
